/**
 * Words and phrases with plainer alternatives.
 *
 * Source: "Use simple words and phrases", in the Federal Plain Language Guidelines
 * (plainlanguage.gov, now maintained by GSA at github.com/GSA/plainlanguage.gov). As a work
 * of the United States Government the page is in the public domain, and GSA also
 * dedicates the repository to the public domain under CC0 1.0.
 *
 * Adapted for this tool, not copied wholesale: entries meant for agency memos rather than
 * prose (a "combat environment", "the undersigned", "your office", slashes) and entries that
 * are ordinary nouns as often as the complex word (address, function, interface, request,
 * type, subject) are left out, two typos in the original are fixed, and verbs are marked so
 * every form (utilize, utilizes, utilized, utilizing) is found.
 *
 * Line format: `word or phrase = alternative, alternative`. A trailing `*` on the word
 * marks a verb whose endings are matched too; a trailing `!` marks one of the guidelines'
 * "dirty dozen", the twelve words most likely to weaken writing. "(omit)" means the word can
 * usually be cut.
 */

const SOURCE = `
  accompany* = go with
  accomplish* = carry out, do
  accordingly = so
  accrue* = add, gain
  accurate = correct, exact, right
  additional = added, more, other
  adjacent to = next to
  advantageous = helpful
  adversely impact on = hurt, set back
  advise* = recommend, tell
  afford an opportunity = allow, let
  allocate* = divide
  anticipate* = expect
  a number of = some
  apparent = clear, plain
  appreciable = many
  appropriate = (omit), proper, right
  approximate = about
  as a means of = to
  ascertain* = find out, learn
  as prescribed by = in, under
  assist*! = aid, help
  assistance! = aid, help
  attain* = meet
  attempt* = try
  at the present time = at present, now
  be advised = (omit)
  benefit* = help
  by means of = by, with
  capability = ability
  caveat = warning
  close proximity = near
  commence*! = begin, start
  comply with = follow
  component = part
  comprise* = form, include, make up
  concerning = about, on
  consequently = so
  consolidate* = combine, join, merge
  constitutes = is, forms, makes up
  contains = has
  convene* = meet
  currently = (omit), now
  deem* = believe, consider, think
  delete* = cut, drop
  demonstrate* = prove, show
  depart* = leave
  designate* = appoint, choose, name
  desire* = want, wish
  determine* = decide, figure, find
  disclose* = show
  discontinue* = drop, stop
  disseminate* = give, issue, pass, send
  due to the fact that = due to, since
  during the period = during
  effect modifications = make changes
  elect* = choose, pick
  eliminate* = cut, drop, end
  employ* = use
  encounter* = meet
  endeavor* = try
  ensure* = make sure
  enumerate* = count
  equitable = fair
  establish* = set up, prove, show
  evidenced = showed
  evident = clear
  exhibit* = show
  expedite* = hasten, speed up
  expeditious = fast, quick
  expend* = spend
  expertise = ability
  expiration = end
  facilitate* = ease, help
  failed to = didn't
  feasible = can be done, workable
  females = women
  finalize* = complete, finish
  for a period of = for
  forfeit* = give up, lose
  frequently = often
  furnish* = give, send
  has a requirement for = needs
  herein = here
  heretofore = until now
  herewith = below, here
  however = but
  identical = same
  identify* = find, name, show
  immediately = at once
  impacted = affected, changed
  implement*! = carry out, start
  in accordance with! = by, following, per, under
  in addition = also, besides, too
  in an effort to = to
  inasmuch as = since
  in a timely manner = on time, promptly
  inception = start
  incumbent upon = must
  indicate* = show, write down
  indication = sign
  initial = first
  initiate* = start
  in lieu of = instead
  in order that! = for, so
  in order to = to
  in regard to = about, concerning, on
  in relation to = about, with, to
  inter alia = (omit)
  in the amount of! = for
  in the event of! = if
  in the near future = shortly, soon
  in the process of = (omit)
  in view of = since
  in view of the above = so
  is applicable to = applies to
  is authorized to = may
  is responsible for = handles
  it appears = seems
  it is essential = must, need to
  it is requested = please, we request, I request
  liaison = discussion
  magnitude = size
  maintain* = keep, support
  maximum = greatest, largest, most
  methodology = method
  minimize* = decrease
  minimum = least, smallest
  modify* = change
  monitor* = check, watch
  necessitate* = cause, need
  notify* = let know, tell
  notwithstanding = in spite of, still
  numerous = many
  objective = aim, goal
  obligate = bind, compel
  observe* = see
  on a regular basis = (omit)
  operate* = run, use, work
  optimum = best, greatest, most
  option = choice, way
  parameters = limits
  participate* = take part
  perform* = do
  permit* = let
  pertaining to = about, of, on
  portion = part
  possess* = have, own
  practicable = practical
  preclude* = prevent
  previous = earlier
  previously = before
  prioritize* = rank
  prior to = before
  proceed = do, go ahead, try
  procure* = (omit)
  proficiency = skill
  promulgate*! = issue, publish
  provide* = give, offer, say
  provided that = if
  provides guidance for = guides
  purchase* = buy
  pursuant to = by, following, per, under
  reflect* = say, show
  regarding = about, of, on
  relative to = about, on
  relocate* = move
  remain* = stay
  remainder = rest
  remuneration = pay, payment
  represents = is
  require* = must, need
  requirement = need
  reside* = live
  retain* = keep
  selection = choice
  set forth in = in
  similar to = like
  solicit* = ask for, request
  state-of-the-art = latest
  submit* = give, send
  subsequent = later, next
  subsequently = after, later, then
  substantial = large, much
  successfully complete = complete, pass
  sufficient = enough
  take action to = (omit)
  terminate* = end, stop
  therefore = so
  therein = there
  thereof = its, their
  the use of = (omit)
  timely = prompt
  transmit* = send
  under the provisions of = under
  until such time as = until
  utilize*! = use
  utilization! = use
  validate* = confirm
  viable = practical, workable
  warrant* = call for, permit
  whereas = because, since
  with reference to = about
  with the exception of = except for
  witnessed = saw
`;

export const OMIT = "(omit)";

export interface PlainEntry {
  /** The complex word or phrase, lowercase. For a verb, the base form. */
  word: string;
  /** Plainer alternatives, in the guidelines' order. May include OMIT. */
  alternatives: string[];
  /** Match the verb's endings too (-s, -ed, -ing). */
  verb: boolean;
  /** One of the guidelines' "dirty dozen". */
  dirtyDozen: boolean;
}

const RE_LINES = /\r?\n/;
const RE_MARKERS = /[*!]+$/;

export const PLAIN_ENTRIES: readonly PlainEntry[] = SOURCE.split(RE_LINES)
  .map((line) => line.trim())
  .filter((line) => line.length > 0)
  .map((line) => {
    const [left, right] = line.split(" = ");
    const markers = RE_MARKERS.exec(left)?.[0] ?? "";
    return {
      word: left.replace(RE_MARKERS, "").trim(),
      alternatives: right.split(",").map((alt) => alt.trim()),
      verb: markers.includes("*"),
      dirtyDozen: markers.includes("!"),
    };
  });
